import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Stack,
  Paper,
  Avatar,
  Chip,
  Button,
  IconButton,
  Divider,
  Typography,
  Tooltip,
  TextField,
  MenuItem,
  InputAdornment,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";

import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";

/* ─── Schema ───────────────────────────────────────────────────────────── */

// parent_V2(id, fullName, email, password, parentToken, cell, status, date)
const PARENT_COLUMNS = [
  "fullName",
  "email",
  "password",
  "parentToken",
  "cell",
  "status",
  "date",
];

// Roles allowed to add, edit and delete parents. Spelling follows the route guard
// in App.js ("supperAdmin" and "manager" are the values the API actually returns).
const PARENT_MANAGER_ROLES = [
  "hr",
  "admin",
  "supperAdmin",
  "manager",
  "ceo",
  "coo",
];

// Sentinel values for the dropdown filters.
const ALL = "__all__";
const LINKED = "__linked__";
const UNLINKED = "__unlinked__";

/* ─── Helpers ──────────────────────────────────────────────────────────── */

const today = () => new Date().toISOString().split("T")[0];

const generateToken = () =>
  Math.random().toString(36).substring(2, 15) +
  Math.random().toString(36).substring(2, 15);

const isActiveParent = (p) => String(p?.status) === "1";

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// addData / updateData respond with the whole table, not just the row that was
// inserted, so a new record is located by the email that was just submitted.
// Searched from the end because the newest row is normally appended last.
const findRowByEmail = (rows, email) => {
  if (!Array.isArray(rows)) return null;
  const target = String(email || "").trim().toLowerCase();
  if (!target) return null;

  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (String(rows[i]?.email || "").trim().toLowerCase() === target) {
      return rows[i];
    }
  }
  return null;
};

const initials = (fullName) => {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

const EMPTY_FORM = {
  fullName: "",
  email: "",
  password: "",
  parentToken: "",
  cell: "",
  status: "1",
  date: today(),
};

/* ─── Presentational components ────────────────────────────────────────── */
// Defined at module scope on purpose. A component declared inside Parents would
// be a brand-new type on every render, so React would unmount and remount these
// subtrees on each keystroke and the focused input would lose focus after one
// character.

const FormSection = ({ ui, title, hint, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography
      variant="h6"
      sx={{
        mb: hint ? 0.35 : 1.5,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        fontWeight: 700,
        color: ui.accent,
      }}
    >
      {title}
    </Typography>
    {hint && (
      <Typography variant="h6" sx={{ mb: 1.5, color: ui.textSecondary }}>
        {hint}
      </Typography>
    )}
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

const DetailRow = ({ ui, label, value, color }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="h6"
      sx={{
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: ui.textSecondary,
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h5"
      sx={{
        fontWeight: 600,
        color: color || ui.textPrimary,
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

/* ─────────────────────────────────────────────────────────────────────── */

const Parents = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { getData, addData, updateData, deleteData, userRole } = useAuth();

  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [sortBy, setSortBy] = useState("newest");
  const [linkFilter, setLinkFilter] = useState(ALL);

  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [parentToDelete, setParentToDelete] = useState(null);

  const [formData, setFormData] = useState(EMPTY_FORM);

  const canManageParents = PARENT_MANAGER_ROLES.includes(userRole);

  /* ---------------- Derived design tokens ---------------- */
  const ui = useMemo(() => {
    const accent = colors.teal[500];
    const accent2 = colors.newTeal[500];
    return {
      accent,
      accent2,
      pageBg: isDark ? colors.dark[800] : "#e9ebef",
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
      shadow: isDark
        ? "0 14px 34px -22px rgba(0,0,0,0.8)"
        : "0 14px 34px -24px rgba(16,24,74,0.35)",
    };
  }, [colors, isDark]);

  /* ---------------- Data ---------------- */
  const fetchData = useCallback(async () => {
    try {
      const [parentsResponse, studentsResponse] = await Promise.all([
        getData("proccess.php", "parent_V2"),
        getData("proccess.php", "students_V2"),
      ]);

      if (parentsResponse.success) {
        setParents(
          Array.isArray(parentsResponse.data) ? parentsResponse.data : []
        );
      } else {
        toast.error(parentsResponse.message || "Failed to load parent data");
      }

      // Only used to show what each parent is linked to — a failure here should
      // not block the page.
      if (studentsResponse.success && Array.isArray(studentsResponse.data)) {
        setStudents(studentsResponse.data);
      }
    } catch (err) {
      toast.error("Network error: Failed to fetch parent data");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [getData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // parentId -> the students registered against it
  const studentsByParent = useMemo(() => {
    const map = new Map();
    students.forEach((s) => {
      if (!s.parent_id) return;
      const key = String(s.parent_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    return map;
  }, [students]);

  const childrenOf = useCallback(
    (parentId) => studentsByParent.get(String(parentId)) || [],
    [studentsByParent]
  );

  /* ---------------- Filtering & sorting ---------------- */
  const visibleParents = useMemo(() => {
    const q = search.trim().toLowerCase();

    return parents
      .filter((p) => {
        if (statusFilter === "all") return true;
        return statusFilter === "active"
          ? isActiveParent(p)
          : !isActiveParent(p);
      })
      .filter((p) => {
        if (linkFilter === ALL) return true;
        const count = childrenOf(p.id).length;
        return linkFilter === LINKED ? count > 0 : count === 0;
      })
      .filter((p) => {
        if (!q) return true;
        return [p.fullName, p.email, p.cell, p.parentToken]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
      .sort((a, b) => {
        if (sortBy === "name") {
          return String(a.fullName || "").localeCompare(String(b.fullName || ""));
        }
        if (sortBy === "students") {
          return childrenOf(b.id).length - childrenOf(a.id).length;
        }
        const byDate = new Date(b.date || 0) - new Date(a.date || 0);
        if (byDate !== 0) return byDate;
        return Number(b.id) - Number(a.id);
      });
  }, [parents, search, statusFilter, linkFilter, sortBy, childrenOf]);

  const counts = useMemo(
    () => ({
      active: parents.filter(isActiveParent).length,
      inactive: parents.filter((p) => !isActiveParent(p)).length,
      all: parents.length,
    }),
    [parents]
  );

  /* ---------------- Export ---------------- */
  const exportToExcel = () => {
    if (visibleParents.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const rows = visibleParents.map((parent) => ({
        ID: parent.id,
        "Full Name": parent.fullName || "",
        Email: parent.email || "",
        Cell: parent.cell || "",
        Status: isActiveParent(parent) ? "Active" : "Inactive",
        "Linked Students": childrenOf(parent.id).length,
        "Student Names": childrenOf(parent.id)
          .map((s) => `${s.firstName || ""} ${s.lastName || ""}`.trim())
          .join("; "),
        "Parent Token": parent.parentToken || "",
        "Date Created": parent.date || "",
      }));

      const headers = Object.keys(rows[0]);
      const csv = [
        headers.join(","),
        ...rows.map((row) =>
          headers
            .map((h) => `"${String(row[h] ?? "").replace(/"/g, '""')}"`)
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `parents_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${visibleParents.length} parent(s)`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };

  /* ---------------- Dialog handlers ---------------- */
  const handleOpenDialog = (isEdit = false, parent = null) => {
    setIsEditing(isEdit);
    setSelectedParentId(isEdit ? parent.id : null);

    if (isEdit && parent) {
      setFormData({
        fullName: parent.fullName || "",
        email: parent.email || "",
        password: "", // never pre-filled
        parentToken: parent.parentToken || generateToken(),
        cell: parent.cell || "",
        status: parent.status !== undefined ? String(parent.status) : "1",
        date: parent.date || today(),
      });
    } else {
      setFormData({ ...EMPTY_FORM, parentToken: generateToken() });
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedParentId(null);
    setFormData(EMPTY_FORM);
  };

  // Clicking the backdrop must not discard a part-filled form — the dialog only
  // closes via Cancel, Escape, or a successful save.
  const handleDialogClose = (_event, reason) => {
    if (reason === "backdropClick") return;
    handleCloseDialog();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* ---------------- Validation & payload ---------------- */
  // The submit button is not a native form submit, so `required` alone would never
  // fire — everything mandatory is checked here.
  const validate = () => {
    const required = [
      ["fullName", "Full name"],
      ["email", "Email"],
      ["cell", "Cell"],
      ["date", "Date created"],
    ];

    for (const [key, label] of required) {
      if (!String(formData[key] ?? "").trim()) {
        toast.error(`${label} is required`);
        return false;
      }
    }

    if (!isEditing && !formData.password) {
      toast.error("Password is required");
      return false;
    }

    const email = formData.email.trim().toLowerCase();
    const clash = parents.find(
      (p) =>
        String(p.email || "").trim().toLowerCase() === email &&
        String(p.id) !== String(selectedParentId)
    );
    if (clash) {
      toast.error(`"${formData.email}" is already used by ${clash.fullName}`);
      return false;
    }

    return true;
  };

  const buildPayload = () =>
    PARENT_COLUMNS.reduce((payload, key) => {
      const value = formData[key];
      // Leave the stored password alone when the field is left blank on edit.
      if (key === "password" && isEditing && !value) return payload;
      payload[key] = typeof value === "string" ? value.trim() : value;
      return payload;
    }, {});

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    const payload = buildPayload();
    setSaving(true);

    try {
      const response = isEditing
        ? await updateData(
            "proccess.php",
            "parent_V2",
            payload,
            selectedParentId
          )
        : await addData("proccess.php", "parent_V2", payload);

      if (!response.success) {
        toast.error(response.message || "Operation failed");
        return; // keep the dialog open so the captured input is not lost
      }

      // The response carries every parent, so prefer it over patching local state.
      if (Array.isArray(response.data)) {
        setParents(response.data);
      } else if (isEditing) {
        setParents((prev) =>
          prev.map((p) =>
            String(p.id) === String(selectedParentId) ? { ...p, ...payload } : p
          )
        );
      }

      if (isEditing) {
        toast.success("Parent updated");
      } else {
        const created = findRowByEmail(response.data, payload.email);
        if (!Array.isArray(response.data)) {
          setParents((prev) => [...prev, { ...payload, id: created?.id }]);
        }
        toast.success(
          created?.id ? `Parent added (#${created.id})` : "Parent added"
        );
      }

      handleCloseDialog();
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Save error:", err);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteData(
        "proccess.php",
        "parent_V2",
        parentToDelete.id
      );
      if (response.success) {
        toast.success("Parent deleted");
        if (Array.isArray(response.data)) setParents(response.data);
        else
          setParents((prev) =>
            prev.filter((p) => String(p.id) !== String(parentToDelete.id))
          );
        setSelectedParent(null);
      } else {
        toast.error(response.message || "Failed to delete parent");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete parent");
      console.error("Delete error:", err);
    }
    setParentToDelete(null);
  };

  /* ---------------- Shared style helpers ---------------- */
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      color: ui.textPrimary,
      bgcolor: ui.surfaceAlt,
      "& fieldset": { borderColor: ui.border },
      "&:hover fieldset": { borderColor: alpha(ui.accent, 0.5) },
      "&.Mui-focused fieldset": { borderColor: ui.accent },
    },
    "& .MuiInputLabel-root": { color: ui.textSecondary },
    "& .MuiInputLabel-root.Mui-focused": { color: ui.accent },
    "& .MuiFormHelperText-root": { color: ui.textSecondary },
  };

  const primaryBtn = {
    px: 2.75,
    py: 1.1,
    borderRadius: 2,
    fontWeight: 700,
    textTransform: "none",
    color: "#ffffff",
    background: ui.gradient,
    boxShadow: `0 10px 24px -12px ${alpha(ui.accent, 0.9)}`,
    "&:hover": { filter: "brightness(1.06)" },
  };

  const outlineBtn = (color) => ({
    px: 2.5,
    py: 1.05,
    borderRadius: 2,
    fontWeight: 700,
    textTransform: "none",
    color,
    border: `1px solid ${alpha(color, 0.45)}`,
    "&:hover": { bgcolor: alpha(color, 0.1), borderColor: color },
  });

  /* ------------------------------------------------------------------ */

  return (
    <Box sx={{ bgcolor: ui.pageBg, minHeight: "100%", pb: 4 }}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <Container maxWidth="xl" sx={{ py: 3.5 }}>
        {/* ------------------------------------------------------------ */}
        {/* Header                                                        */}
        {/* ------------------------------------------------------------ */}
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                letterSpacing: -0.4,
                color: ui.textPrimary,
              }}
            >
              Parents
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
              Click any parent for full details and linked students
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              size="small"
              placeholder="Search name, email, cell…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ ...fieldSx, minWidth: { sm: 260 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 19, color: ui.textSecondary }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              sx={outlineBtn(colors.green[500])}
            >
              Export
            </Button>
            <Tooltip
              title={canManageParents ? "" : "Your role cannot add parents"}
            >
              <span>
                <Button
                  startIcon={<AddCircleOutlineOutlinedIcon />}
                  onClick={() => handleOpenDialog(false)}
                  disabled={!canManageParents}
                  sx={primaryBtn}
                >
                  Add parent
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* ------------------------------------------------------------ */}
        {/* Filters                                                       */}
        {/* ------------------------------------------------------------ */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <ToggleButtonGroup
            exclusive
            value={statusFilter}
            onChange={(_, v) => v !== null && setStatusFilter(v)}
            sx={{
              flexWrap: "wrap",
              gap: 1,
              "& .MuiToggleButton-root": {
                px: 2,
                py: 0.75,
                border: `1px solid ${ui.border} !important`,
                borderRadius: "999px !important",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 13,
                color: ui.textSecondary,
                bgcolor: ui.surface,
                "&.Mui-selected": {
                  color: ui.accent,
                  bgcolor: alpha(ui.accent, 0.14),
                  borderColor: `${alpha(ui.accent, 0.5)} !important`,
                  "&:hover": { bgcolor: alpha(ui.accent, 0.2) },
                },
              },
            }}
          >
            {[
              { key: "active", label: "Active", count: counts.active },
              { key: "inactive", label: "Inactive", count: counts.inactive },
              { key: "all", label: "All", count: counts.all },
            ].map((f) => (
              <ToggleButton key={f.key} value={f.key}>
                {f.label}
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    px: 0.85,
                    borderRadius: 999,
                    fontSize: 11,
                    bgcolor: alpha(ui.textSecondary, 0.18),
                  }}
                >
                  {f.count}
                </Box>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <TextField
              select
              size="small"
              label="Linked students"
              value={linkFilter}
              onChange={(e) => setLinkFilter(e.target.value)}
              sx={{ ...fieldSx, minWidth: { sm: 190 } }}
            >
              <MenuItem value={ALL}>All parents</MenuItem>
              <MenuItem value={LINKED}>Has students</MenuItem>
              <MenuItem value={UNLINKED}>No students</MenuItem>
            </TextField>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={sortBy}
              onChange={(_, v) => v !== null && setSortBy(v)}
              sx={{
                gap: 1,
                "& .MuiToggleButton-root": {
                  px: 1.75,
                  py: 0.6,
                  border: `1px solid ${ui.border} !important`,
                  borderRadius: "999px !important",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 12.5,
                  color: ui.textSecondary,
                  bgcolor: ui.surface,
                  "&.Mui-selected": {
                    color: ui.accent,
                    bgcolor: alpha(ui.accent, 0.14),
                    borderColor: `${alpha(ui.accent, 0.5)} !important`,
                  },
                },
              }}
            >
              <ToggleButton value="newest">Newest</ToggleButton>
              <ToggleButton value="name">A–Z</ToggleButton>
              <ToggleButton value="students">Most students</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>

        <Typography variant="h6" sx={{ mb: 2, color: ui.textSecondary }}>
          Showing {visibleParents.length} of {parents.length} parent(s)
        </Typography>

        {/* ------------------------------------------------------------ */}
        {/* Cards                                                         */}
        {/* ------------------------------------------------------------ */}
        {loading ? (
          <Grid container spacing={2.5}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={`sk-${i}`}>
                <Skeleton
                  variant="rounded"
                  height={236}
                  sx={{
                    borderRadius: 3,
                    bgcolor: alpha(ui.textSecondary, 0.12),
                  }}
                />
              </Grid>
            ))}
          </Grid>
        ) : visibleParents.length === 0 ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 10 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: ui.textPrimary }}
            >
              No parents found
            </Typography>
            <Typography variant="h6" sx={{ color: ui.textSecondary }}>
              {search
                ? "Nothing matches your search."
                : "Add a parent to begin."}
            </Typography>
          </Stack>
        ) : (
          <Grid container spacing={2.5}>
            {visibleParents.map((parent) => {
              const active = isActiveParent(parent);
              const linked = childrenOf(parent.id);
              const accent = active ? ui.accent2 : colors.gray[500];

              return (
                <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={parent.id}>
                  <Paper
                    elevation={0}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedParent(parent)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedParent(parent);
                      }
                    }}
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      borderRadius: 3,
                      overflow: "hidden",
                      textAlign: "center",
                      bgcolor: ui.surface,
                      border: `1px solid ${ui.border}`,
                      boxShadow: ui.shadow,
                      opacity: active ? 1 : 0.72,
                      transition:
                        "transform .2s ease, border-color .2s ease, box-shadow .2s ease",
                      "&:hover, &:focus-visible": {
                        outline: "none",
                        transform: "translateY(-4px)",
                        borderColor: alpha(accent, 0.5),
                        boxShadow: `0 16px 32px -20px ${alpha(accent, 0.9)}`,
                      },
                    }}
                  >
                    {/* Banner */}
                    <Box
                      sx={{
                        height: 62,
                        background: `linear-gradient(135deg, ${alpha(
                          accent,
                          isDark ? 0.3 : 0.22
                        )} 0%, ${alpha(
                          ui.accent,
                          isDark ? 0.18 : 0.12
                        )} 100%)`,
                      }}
                    />

                    <Box sx={{ px: 2.5, pb: 2.5, mt: -4.5 }}>
                      <Box
                        sx={{ position: "relative", display: "inline-block" }}
                      >
                        <Avatar
                          sx={{
                            width: 76,
                            height: 76,
                            fontSize: 24,
                            fontWeight: 700,
                            bgcolor: ui.surfaceAlt,
                            color: ui.textPrimary,
                            border: `3px solid ${ui.surface}`,
                            boxShadow: ui.shadow,
                          }}
                        >
                          {initials(parent.fullName)}
                        </Avatar>

                        {active && (
                          <Tooltip title="Active">
                            <VerifiedOutlinedIcon
                              sx={{
                                position: "absolute",
                                right: 2,
                                bottom: 2,
                                fontSize: 20,
                                color: colors.green[500],
                                bgcolor: ui.surface,
                                borderRadius: "50%",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>

                      <Typography
                        variant="h4"
                        noWrap
                        sx={{
                          mt: 1.25,
                          fontWeight: 700,
                          color: ui.textPrimary,
                        }}
                      >
                        {parent.fullName || "Unnamed parent"}
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={0.75}
                        justifyContent="center"
                        sx={{ mt: 1, mb: 1.75, flexWrap: "wrap", gap: 0.75 }}
                      >
                        <Chip
                          size="small"
                          icon={<SchoolOutlinedIcon sx={{ fontSize: 15 }} />}
                          label={
                            linked.length === 1
                              ? "1 student"
                              : `${linked.length} students`
                          }
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            color: linked.length ? ui.accent : ui.textSecondary,
                            bgcolor: alpha(
                              linked.length ? ui.accent : ui.textSecondary,
                              0.14
                            ),
                            "& .MuiChip-icon": { color: "inherit" },
                          }}
                        />
                        {!active && (
                          <Chip
                            size="small"
                            label="Inactive"
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              fontSize: "0.68rem",
                              color: colors.red[500],
                              bgcolor: alpha(colors.red[500], 0.14),
                            }}
                          />
                        )}
                      </Stack>

                      <Divider sx={{ borderColor: ui.border, mb: 1.5 }} />

                      <Stack spacing={0.85} sx={{ textAlign: "left" }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <MailOutlineIcon
                            sx={{
                              fontSize: 15,
                              color: ui.textSecondary,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="h6"
                            noWrap
                            sx={{ color: ui.textSecondary }}
                          >
                            {parent.email || "—"}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneIphoneIcon
                            sx={{
                              fontSize: 15,
                              color: ui.textSecondary,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="h6"
                            noWrap
                            sx={{ color: ui.textSecondary }}
                          >
                            {parent.cell || "—"}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EventOutlinedIcon
                            sx={{
                              fontSize: 15,
                              color: ui.textSecondary,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="h6"
                            noWrap
                            sx={{ color: ui.textSecondary }}
                          >
                            {fmtDate(parent.date)}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* -------------------------------------------------------------- */}
      {/* Detail dialog                                                   */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={!!selectedParent}
        onClose={() => setSelectedParent(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        {selectedParent && (
          <>
            <Box
              sx={{
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 2.25,
                background: `linear-gradient(180deg, ${alpha(
                  ui.accent2,
                  isDark ? 0.18 : 0.11
                )} 0%, transparent 100%)`,
              }}
            >
              <Avatar
                sx={{
                  width: 78,
                  height: 78,
                  fontSize: 26,
                  fontWeight: 700,
                  bgcolor: ui.surfaceAlt,
                  color: ui.textPrimary,
                  border: `2px solid ${alpha(ui.accent2, 0.5)}`,
                }}
              >
                {initials(selectedParent.fullName)}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 800, color: ui.textPrimary }}
                >
                  {selectedParent.fullName || "Unnamed parent"}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 0.85, flexWrap: "wrap", gap: 0.75 }}
                >
                  <Chip
                    size="small"
                    label={
                      isActiveParent(selectedParent) ? "Active" : "Inactive"
                    }
                    sx={{
                      fontWeight: 700,
                      color: isActiveParent(selectedParent)
                        ? colors.green[500]
                        : colors.red[500],
                      bgcolor: alpha(
                        isActiveParent(selectedParent)
                          ? colors.green[500]
                          : colors.red[500],
                        0.14
                      ),
                    }}
                  />
                  <Chip
                    size="small"
                    icon={<FamilyRestroomOutlinedIcon sx={{ fontSize: 15 }} />}
                    label={`${childrenOf(selectedParent.id).length} linked student(s)`}
                    sx={{
                      fontWeight: 700,
                      color: ui.accent,
                      bgcolor: alpha(ui.accent, 0.14),
                      "& .MuiChip-icon": { color: "inherit" },
                    }}
                  />
                </Stack>
              </Box>

              <IconButton
                onClick={() => setSelectedParent(null)}
                sx={{
                  color: ui.textSecondary,
                  "&:hover": {
                    color: colors.red[500],
                    bgcolor: alpha(colors.red[500], 0.12),
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ borderColor: ui.border }} />

            <DialogContent>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1.5,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: ui.accent,
                    }}
                  >
                    Account
                  </Typography>
                  <DetailRow ui={ui} label="Email" value={selectedParent.email} />
                  <DetailRow ui={ui} label="Cell" value={selectedParent.cell} />
                  <DetailRow
                    ui={ui}
                    label="Status"
                    value={isActiveParent(selectedParent) ? "Active" : "Inactive"}
                    color={
                      isActiveParent(selectedParent)
                        ? colors.green[500]
                        : colors.red[500]
                    }
                  />
                  <DetailRow
                    ui={ui}
                    label="Registered"
                    value={fmtDate(selectedParent.date)}
                  />
                  <DetailRow
                    ui={ui}
                    label="Parent token"
                    value={selectedParent.parentToken}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1.5,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: ui.accent,
                    }}
                  >
                    Linked students
                  </Typography>

                  {childrenOf(selectedParent.id).length === 0 ? (
                    <Typography
                      variant="h5"
                      sx={{ color: ui.textSecondary, fontWeight: 600 }}
                    >
                      No students are linked to this parent.
                    </Typography>
                  ) : (
                    <Stack spacing={1.25}>
                      {childrenOf(selectedParent.id).map((s) => (
                        <Box
                          key={s.id}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: `1px solid ${ui.border}`,
                            bgcolor: alpha(ui.surfaceAlt, isDark ? 0.5 : 0.7),
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, color: ui.textPrimary }}
                          >
                            {s.firstName} {s.lastName}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ color: ui.textSecondary }}
                          >
                            {s.school || "No school"}
                            {s.grade ? ` · Grade ${s.grade}` : ""}
                            {s.parent_relationship
                              ? ` · ${s.parent_relationship}`
                              : ""}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Grid>
              </Grid>
            </DialogContent>

            <Divider sx={{ borderColor: ui.border }} />

            <DialogActions sx={{ px: 3, py: 2.5, flexWrap: "wrap", gap: 1 }}>
              <Button
                startIcon={<DeleteForeverOutlinedIcon />}
                onClick={() => setParentToDelete(selectedParent)}
                disabled={!canManageParents}
                sx={{ ...outlineBtn(colors.red[500]), mr: "auto" }}
              >
                Delete
              </Button>

              <Button
                startIcon={<EditNoteOutlinedIcon />}
                onClick={() => {
                  const parent = selectedParent;
                  setSelectedParent(null);
                  handleOpenDialog(true, parent);
                }}
                disabled={!canManageParents}
                sx={primaryBtn}
              >
                Edit
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* -------------------------------------------------------------- */}
      {/* Add / Edit dialog                                               */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, color: ui.textPrimary }}
          >
            {isEditing ? "Edit parent" : "Add parent"}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1.5 }}>
            <FormSection
              ui={ui}
              title="Account"
              hint="The email is the parent's portal login and must be unique."
            >
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Full name"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="tel"
                  label="Cell"
                  name="cell"
                  value={formData.cell}
                  onChange={handleInputChange}
                  placeholder="081XXXXXXX"
                  inputProps={{ maxLength: 15 }}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="password"
                  label="Password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!isEditing}
                  helperText={
                    isEditing ? "Leave blank to keep current password" : " "
                  }
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                >
                  <MenuItem value="1">Active</MenuItem>
                  <MenuItem value="0">Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date created"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Parent token"
                  name="parentToken"
                  value={formData.parentToken}
                  onChange={handleInputChange}
                  helperText="Generated automatically — change only if you must"
                  sx={fieldSx}
                />
              </Grid>
            </FormSection>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: ui.textSecondary,
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} sx={primaryBtn}>
            {saving ? "Saving…" : isEditing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------------------------------------------- */}
      {/* Delete dialog                                                   */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={!!parentToDelete}
        onClose={() => setParentToDelete(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, color: ui.textPrimary }}
          >
            Delete parent
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="h5"
            sx={{ color: ui.textSecondary, lineHeight: 1.6 }}
          >
            Permanently delete{" "}
            <Box
              component="span"
              sx={{ fontWeight: 800, color: ui.textPrimary }}
            >
              {parentToDelete?.fullName}
            </Box>
            ? This cannot be undone. To keep their records, set their status to
            Inactive instead.
          </Typography>

          {parentToDelete && childrenOf(parentToDelete.id).length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(colors.red[500], 0.45)}`,
                bgcolor: alpha(colors.red[500], 0.1),
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: colors.red[500] }}
              >
                {childrenOf(parentToDelete.id).length} student(s) are still
                linked to this parent
              </Typography>
              <Typography
                variant="h6"
                sx={{ mt: 0.5, color: ui.textSecondary }}
              >
                {childrenOf(parentToDelete.id)
                  .map((s) => `${s.firstName || ""} ${s.lastName || ""}`.trim())
                  .join(", ")}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setParentToDelete(null)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: ui.textSecondary,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            startIcon={<DeleteForeverOutlinedIcon />}
            sx={{
              px: 2.75,
              py: 1.1,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: colors.red[500],
              "&:hover": { bgcolor: colors.red[600] },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Parents;
